/**
 * Wishlist Account Sync — back-sync to customer metafield
 *
 * Loaded (deferred) immediately after wishlist.js. When a customer is logged in,
 * this patches MinimogTheme.Wishlist.saveToStorage so that every wishlist change
 * is also pushed to the Shopify App Proxy endpoint (/apps/wishlist-sync), which
 * persists the list to the customer metafield wishlist.items.
 *
 * Consistency on fast navigation:
 *   - Removals are recorded as short-lived "tombstones" (via window.__wlSync,
 *     defined in snippets/wishlist-customer-data.liquid) so that if the metafield
 *     write has not propagated yet, the head-snippet merge won't resurrect a
 *     just-removed item on the next page load.
 *   - Any pending (debounced) sync is flushed with navigator.sendBeacon on
 *     pagehide / visibilitychange so a quick reload or navigation cannot drop a
 *     change that was still waiting on the debounce timer.
 *
 * The merge of the account wishlist into localStorage on page load is handled
 * separately and synchronously by snippets/wishlist-customer-data.liquid.
 */
(function () {
  'use strict';

  var PROXY_URL = '/apps/wishlist-sync';
  var STORAGE_KEY = 'm-wishlist-products';
  var SYNC_DEBOUNCE_MS = 600;

  var account = window.__wishlistAccount || { loggedIn: false };

  // Only sync for logged-in customers; guests stay on localStorage only.
  if (!account.loggedIn) return;

  var sync = window.__wlSync || {};
  var syncTimer = null;
  var pendingSync = false;

  function readLocal() {
    try {
      var raw = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
      return Array.isArray(raw) ? raw : [];
    } catch (e) {
      return [];
    }
  }

  function getProducts() {
    try {
      var wl = window.MinimogTheme && window.MinimogTheme.Wishlist;
      if (wl && Array.isArray(wl.products)) {
        return Array.from(new Set(wl.products));
      }
    } catch (e) {}
    return Array.from(new Set(readLocal()));
  }

  function sendSync(products, useBeacon) {
    var payload = JSON.stringify({ wishlist: products });

    // Prefer sendBeacon during unload — it is guaranteed to be queued even as
    // the page goes away, unlike a debounced fetch.
    if (useBeacon && navigator.sendBeacon) {
      try {
        var blob = new Blob([payload], { type: 'application/json' });
        if (navigator.sendBeacon(PROXY_URL, blob)) {
          pendingSync = false;
          return;
        }
      } catch (e) {}
    }

    try {
      fetch(PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true
      }).catch(function () {});
      pendingSync = false;
    } catch (e) {}
  }

  function pushSync(useBeacon) {
    if (syncTimer) {
      clearTimeout(syncTimer);
      syncTimer = null;
    }
    sendSync(getProducts(), useBeacon);
  }

  function scheduleSync() {
    pendingSync = true;
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(function () { pushSync(false); }, SYNC_DEBOUNCE_MS);
  }

  function flushSync() {
    if (pendingSync) pushSync(true);
  }

  function patchSaveToStorage() {
    var wl = window.MinimogTheme && window.MinimogTheme.Wishlist;
    if (!wl || typeof wl.saveToStorage !== 'function') return false;
    if (wl.__accountSyncPatched) return true;

    var original = wl.saveToStorage;
    wl.saveToStorage = function () {
      var before = readLocal();
      var result = original.apply(wl, arguments);
      var after = getProducts();

      // Tombstone removals (prevents resurrection before the metafield catches
      // up) and clear tombstones for re-added items.
      var removed = before.filter(function (h) { return after.indexOf(h) === -1; });
      var added = after.filter(function (h) { return before.indexOf(h) === -1; });
      if (removed.length && sync.addTombstones) sync.addTombstones(removed);
      if (added.length && sync.clearTombstones) sync.clearTombstones(added);

      scheduleSync();
      return result;
    };
    wl.__accountSyncPatched = true;
    return true;
  }

  // wishlist.js is deferred and runs before this script, so MinimogTheme.Wishlist
  // should already exist. Retry briefly as a safety net against load-order races.
  if (!patchSaveToStorage()) {
    var attempts = 0;
    var interval = setInterval(function () {
      attempts += 1;
      if (patchSaveToStorage() || attempts > 50) {
        clearInterval(interval);
      }
    }, 100);
  }

  // Flush any pending (debounced) sync before the page is hidden/unloaded.
  window.addEventListener('pagehide', flushSync);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flushSync();
  });

  // If the head snippet merged pre-login local items into the account list, or
  // filtered out recently-removed items the metafield still contains, persist
  // that corrected list to the account now.
  if (account.needsSync) {
    scheduleSync();
  }
})();
